import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, ToastController, ViewController } from 'ionic-angular';
import { AppConfig } from '../../../../app/app.config';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { HttpEventType } from '@angular/common/http';
import {
  JackingActivityFileUploadService
} from '../../../../services/jacking-activity/jacking-activity-file-upload.service';
import {
  JackingArrivalPositioningFile
} from '../../../../models/projects/jacking-activity.models';

export type JackingUploadTarget =
  | { kind: 'v1'; jackingActivityId: number }
  | { kind: 'v2'; jackingFormInstanceId: number };


@IonicPage()
@Component({
  selector: 'page-jacking-activity-file-upload-modal',
  templateUrl: 'jacking-activity-file-upload-modal.html',
})
export class JackingActivityFileUploadModalComponent {

  config = AppConfig.settings;
  jackingActivityId: number;
  jackingFormInstanceId: number;
  loading: boolean;
  fileUploadingPercent: number;
  uploadedFiles: JackingArrivalPositioningFile[] = [];
  showAlert: boolean;
  alertText: string;
  target: JackingUploadTarget;

  constructor(
    private navCtrl: NavController,
    private navParams: NavParams,
    private jackingActivityFileUploadService: JackingActivityFileUploadService,
    private toastCtrl: ToastController,
    private viewController: ViewController) {
    const jackingActivityId = this.navParams.get('jackingActivityId');
    const jackingFormInstanceId = this.navParams.get('jackingFormInstanceId');

    if (jackingFormInstanceId != null) {
      this.target = {kind: 'v2', jackingFormInstanceId};
    } else if (jackingActivityId != null) {
      this.target = {kind: 'v1', jackingActivityId};
    }
  }

  ionViewDidLoad() {
    this.loadFileList();
  }

  closeModal() {
    this.viewController.dismiss();
  }

  public onFileClick(uploadedFile: JackingArrivalPositioningFile): void {
    if (!uploadedFile || !uploadedFile.guid) {
      return;
    }

    this.loading = true;
    this.jackingActivityFileUploadService
      .downloadFile(uploadedFile.guid)
      .subscribe((blob: Blob) => {
        const file = new Blob([blob], {type: 'application/pdf'});
        const fileURL = URL.createObjectURL(file);

        const link = document.createElement('a');
        link.href = fileURL;
        link.download = uploadedFile.name + uploadedFile.extension;
        link.click();

        this.loading = false;
      });
  }

  onFileRemove(item: any) {
    this.loading = true;
    this.jackingActivityFileUploadService.deleteFile(item.id).subscribe({
      next: (res) => {
        const index = this.uploadedFiles.findIndex(
          (f) => f.guid === item.guid
        );

        if (index === -1) {
          return;
        }

        this.uploadedFiles.splice(index, 1);
        this.presentDeleteToast();
        this.loading = false;
      }
    });


  }

  fileDropped(event: NgxFileDropEntry[]) {
    this.showAlert = false;

    if (!this.target) {
      this.showAlert = true;
      this.alertText = 'Error: missing upload context';
      return;
    }

    for (const dropped of event) {
      const allowed = this.checkAllowedFileExtension(dropped.fileEntry.name);

      if (!dropped.fileEntry.isFile) {
        continue;
      }

      if (!allowed) {
        this.showAlert = true;
        this.alertText = 'Incorrect file type. Accepted types are: .png, .jpg, .jpeg, .html, .pdf';
        continue;
      }

      this.uploadFile(dropped);
    }
  }

  private uploadFile(file: NgxFileDropEntry) {
    const fileEntry = file.fileEntry as FileSystemFileEntry;

    fileEntry.file((f: File) => {
      this.jackingActivityFileUploadService.uploadFile(f, this.target).subscribe({
        next: (uploadEvent) => {
          this.fileUploadingPercent = 0;

          switch (uploadEvent.type) {
            case HttpEventType.UploadProgress:
              this.fileUploadingPercent = uploadEvent.total
                ? Math.round((uploadEvent.loaded / uploadEvent.total) * 100)
                : 0;
              break;

            case HttpEventType.Response:
              this.uploadedFiles.push({...uploadEvent.body});
              this.presentUploadToast();
              this.fileUploadingPercent = -1;
              break;
          }
        },
        error: (err) => {
          this.alertText = err.status === 413
            ? 'File size too large. Max size is 5MB'
            : 'An error occurred.';
          this.showAlert = true;
          this.fileUploadingPercent = -1;
        },
        complete: () => {
          this.fileUploadingPercent = -1;
        },
      });
    });
  }

  private checkAllowedFileExtension(filename: string) {
    if (!filename) {
      return false;
    }

    const extension = filename.split('.').pop();

    return extension.toLowerCase() === 'png'
      || extension.toLowerCase() === 'jpg'
      || extension.toLowerCase() === 'jpeg'
      || extension.toLowerCase() === 'html'
      || extension.toLowerCase() === 'pdf';
  }

  private loadFileList() {
    if (!this.target) {
      this.showAlert = true;
      this.alertText = 'Error: missing upload context';
      return;
    }

    this.loading = true;
    this.jackingActivityFileUploadService.getUploadedFiles(this.target).subscribe({
      next: (res) => {
        this.uploadedFiles = res || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showAlert = true;
        this.alertText = 'Failed to load files.';
      }
    });
  }

  private presentUploadToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'File Uploaded',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  private presentDeleteToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'File Removed',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v1/jacking-activity-file-upload-modal/jacking-activity-file-upload-modal.ts